import { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { manewcoTokens } from '@platform/plugin-platform-react';
import AppsIcon from '@material-ui/icons/Apps';
import { Link, SidebarItem, useSidebarOpenState } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { SidebarCollapsibleSection } from './SidebarCollapsibleSection';
import {
  groupApplications,
  OTHER_KEY,
  typeBadge,
  type AppEntity,
  type CatalogItem,
} from './applications';

const FIELDS = ['kind', 'metadata.name', 'metadata.namespace', 'metadata.title', 'spec.type', 'spec.system'];

// Maquette « Clarté complète » : sous-entrées 11 px sous un trait vertical, type en pastille 9 px.
const useRowStyles = makeStyles(theme => {
  const MANEWCO = manewcoTokens(theme);
  return {
    children: { borderLeft: `1px solid ${MANEWCO.line}`, margin: '0 16px 4px 33px', paddingLeft: 9 },
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      minHeight: 34,
      padding: '6px 10px',
      borderRadius: 9,
      overflow: 'hidden',
      color: MANEWCO.navText,
      textDecoration: 'none',
      '&:hover': { backgroundColor: MANEWCO.hover, color: MANEWCO.text },
    },
    name: { overflowWrap: 'anywhere', minWidth: 0, fontSize: 11, lineHeight: 1.3 },
    badge: {
      flexShrink: 0,
      fontSize: 9,
      whiteSpace: 'nowrap',
      color: MANEWCO.navText,
      background: MANEWCO.badge,
      borderRadius: 4,
      padding: '2px 5px',
    },
    toggle: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      width: '100%',
      minHeight: 36,
      padding: '8px 10px',
      background: 'none',
      border: 'none',
      borderRadius: 9,
      cursor: 'pointer',
      textAlign: 'left',
      color: MANEWCO.text,
      fontFamily: theme.typography.fontFamily,
      fontSize: 12,
      fontWeight: 700,
      '&:hover': { backgroundColor: MANEWCO.hover },
    },
    count: { fontSize: 9, fontWeight: 600, background: MANEWCO.badge, borderRadius: 4, padding: '2px 5px', color: MANEWCO.navText },
    toggleChevron: { marginLeft: 'auto', fontSize: 16, lineHeight: 1, transition: 'transform 120ms' },
  };
});

const Chevron = ({ open }: { open: boolean }) => (
  <span
    aria-hidden
    style={{
      display: 'inline-block',
      transform: open ? 'none' : 'rotate(-90deg)',
      transition: 'transform 120ms',
      fontSize: '0.6rem',
    }}
  >
    ▾
  </span>
);

const EntityRow = ({ entity, indent = 0 }: { entity: AppEntity; indent?: number }) => {
  const classes = useRowStyles();
  return (
    <Link
      to={`/catalog/${entity.namespace}/${entity.kind.toLowerCase()}/${entity.name}`}
      className={classes.row}
      style={indent ? { paddingLeft: 10 + indent } : undefined}
      title={entity.name}
    >
      <span className={classes.name}>{entity.title ?? entity.name}</span>
      {entity.type !== 'partner' && <span className={classes.badge}>{typeBadge(entity)}</span>}
    </Link>
  );
};

/**
 * Section « Applications » : un système par entrée, avec ses services et ses
 * partenaires (ressources `partner`), alimentés par le catalogue. Un nouveau
 * service ou partenaire y apparaît sans retoucher le menu.
 */
export const ApplicationsSidebarItems = () => {
  const classes = useRowStyles();
  const theme = useTheme();
  const catalogApi = useApi(catalogApiRef);
  const { isOpen } = useSidebarOpenState();
  const [systems, setSystems] = useState<CatalogItem[]>([]);
  const [entities, setEntities] = useState<CatalogItem[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [partnersOpen, setPartnersOpen] = useState<Record<string, boolean>>({});
  const autoExpanded = useRef(false);

  useEffect(() => {
    const load = (kind: string) =>
      catalogApi
        .getEntities({ filter: { kind }, fields: FIELDS })
        .then(({ items }) => items as CatalogItem[])
        .catch(() => [] as CatalogItem[]);
    load('System').then(setSystems);
    Promise.all([load('Component'), load('Resource')]).then(([c, r]) => setEntities([...c, ...r]));
  }, [catalogApi]);

  const groups = useMemo(() => groupApplications(systems, entities), [systems, entities]);

  // Premier système déplié une seule fois, sans écraser ensuite les clics de l'utilisateur.
  useEffect(() => {
    if (!autoExpanded.current && groups.length > 0) {
      autoExpanded.current = true;
      setExpanded(prev => ({ ...prev, [groups[0].key]: true }));
    }
  }, [groups]);

  if (groups.length === 0) return null;

  return (
    <SidebarCollapsibleSection label="Applications">
      {groups.map(group => {
        const isOther = group.key === OTHER_KEY;
        const open = expanded[group.key] ?? false;
        const hasChildren = group.services.length + group.partners.length > 0;
        const partnersExpanded = partnersOpen[group.key] ?? false;
        return (
          <div key={group.key} style={{ width: '100%', boxSizing: 'border-box' }}>
            <div style={{ position: 'relative' }}>
              <SidebarItem
                icon={AppsIcon}
                to={isOther ? undefined : `/catalog/${group.namespace}/system/${group.key}`}
                text={group.label}
                onClick={(e: React.MouseEvent) => {
                  if (isOther) {
                    e.preventDefault();
                    setExpanded(prev => ({ ...prev, [group.key]: !open }));
                  }
                }}
              />
              {isOpen && hasChildren && !isOther && (
                <button
                  type="button"
                  aria-expanded={open}
                  aria-label={`${open ? 'Replier' : 'Déplier'} ${group.label}`}
                  onClick={() => setExpanded(prev => ({ ...prev, [group.key]: !open }))}
                  style={{
                    position: 'absolute',
                    right: 6,
                    top: 0,
                    height: '100%',
                    padding: '0 6px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: theme.palette.navigation.color,
                  }}
                >
                  <Chevron open={open} />
                </button>
              )}
            </div>
            {isOpen && open && (
              <div className={classes.children}>
                {group.services.map(e => (
                  <EntityRow key={`${e.kind}:${e.name}`} entity={e} />
                ))}
                {group.partners.length > 0 && (
                  <>
                    <button
                      type="button"
                      className={classes.toggle}
                      aria-expanded={partnersExpanded}
                      onClick={() => setPartnersOpen(prev => ({ ...prev, [group.key]: !partnersExpanded }))}
                    >
                      Partenaires
                      <span className={classes.count}>{group.partners.length}</span>
                      <span aria-hidden className={classes.toggleChevron} style={{ transform: partnersExpanded ? 'rotate(90deg)' : 'none' }}>
                        ›
                      </span>
                    </button>
                    {partnersExpanded && (
                      <>
                        <Link to="/partenaires" className={classes.row} style={{ paddingLeft: 22 }}>
                          <span className={classes.name} style={{ fontWeight: 600 }}>Tous les partenaires</span>
                        </Link>
                        {group.partners.map(e => (
                          <EntityRow key={`${e.kind}:${e.name}`} entity={e} indent={12} />
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </SidebarCollapsibleSection>
  );
};
